import { createSelector } from '@reduxjs/toolkit';
import { getTotalFenceWidth, setElementProps } from 'helpers/fenceElements';
import { currentBrick } from 'helpers/globals';
import { getCurrentGroup, getLargestId, getItemIndexById } from 'helpers/utils';

import SonevaFullSand from 'assets/img/soneva-full-sand.png';
import SonevaHalfSand from 'assets/img/soneva-half-sand.png';
import SonevaTopSand from 'assets/img/soneva-top-sand.png';
import SonevaFullCreamWhite from 'assets/img/soneva-full-cream-white.png';
import SonevaHalfCreamWhite from 'assets/img/soneva-half-cream-white.png';
import SonevaTopCreamWhite from 'assets/img/soneva-top-cream-white.png';
import SonevaFullGray from 'assets/img/soneva-full-gray.png';
import SonevaHalfGray from 'assets/img/soneva-half-gray.png';
import SonevaTopGray from 'assets/img/soneva-top-gray.png';
import SonevaFullAnthracite from 'assets/img/soneva-full-anthracite.png';
import SonevaHalfAnthracite from 'assets/img/soneva-half-anthracite.png';
import SonevaTopAnthracite from 'assets/img/soneva-top-anthracite.png';
import SonevaFullSaharaBrown from 'assets/img/soneva-full-sahara-brown.png';
import SonevaHalfSaharaBrown from 'assets/img/soneva-half-sahara-brown.png';
import SonevaTopSaharaBrown from 'assets/img/soneva-top-sahara-brown.png';

const selectFence = (state) => state.fence;

export const getFenceGroupDetails = createSelector([selectFence], (fence) => {
  const groups = [];

  fence.groups.forEach((group) => {
    const { elements, settings } = group;
    const totalFenceWidth = getTotalFenceWidth(elements);
    const elementProps = setElementProps(group, totalFenceWidth);

    groups.push({
      id: group.id,
      settings: group.settings,
      totalFenceWidth,
      totalDoorsWidth: elementProps.totalDoorsWidth,
      maxSlope: settings.slopeSizeCm,
      elements: elementProps.elements,
    });
  });

  return groups;
});

export const getCurrentFenceGroupDetails = createSelector([selectFence], (fence) => {
  const currentGroup = getCurrentGroup(fence);
  const totalFenceWidth = getTotalFenceWidth(currentGroup.elements);
  const elementProps = setElementProps(currentGroup, totalFenceWidth);

  return {
    id: currentGroup.id,
    settings: currentGroup.settings,
    totalFenceWidth,
    totalDoorsWidth: elementProps.totalDoorsWidth,
    maxSlope: currentGroup.settings.slopeSizeCm,
    elements: elementProps.elements,
  };
});

export const getLargestElementId = createSelector([selectFence], (fence) => {
  const { elements } = getCurrentGroup(fence);
  return getLargestId(elements);
});

export const getElementIndexById = (id) =>
  createSelector([selectFence], ({ elements }) => {
    let foundIndex = elements.findIndex((element) => element.id === id);

    return foundIndex > -1 ? foundIndex : 0;
  });

export const getElementById = (id) =>
  createSelector([selectFence], ({ elements }) => {
    let foundElement = elements.find((element) => element.id === id);

    return foundElement;
  });

export const getCurrentFenceGroup = createSelector([selectFence], (fence) => {
  return getCurrentGroup(fence);
});

export const getBrickCounts = (fenceDetails) =>
  createSelector([selectFence], (fence) => {
    const totalBricks = {
      normal: 0,
      half: 0,
      topCover: 0,
    };

    const underGroundRatio = 32;
    let connectorBricks = 0;

    fenceDetails.forEach((group) => {
      const groupIndex = getItemIndexById(fence.groups, group.id);
      const { totalFenceWidth, totalDoorsWidth, maxSlope } = fenceDetails[groupIndex];
      const groupBricks = {
        normal: 0,
        half: 0,
        topCover: 0,
      };

      let underGroundPercentage = 0;

      group.elements.forEach((element) => {
        element.bricks.forEach((brick) => {
          switch (brick.type) {
            case 'normal':
              groupBricks.normal++;
              break;
            case 'half':
              groupBricks.half++;
              break;
            default:
              connectorBricks++;
              break;
          }
        });

        if (element.name === 'fundament' || element.name === 'column') {
          groupBricks.topCover += Math.ceil(element.width / currentBrick.topCover.width);
        }
      });

      underGroundPercentage = underGroundRatio / ((totalFenceWidth - totalDoorsWidth) / maxSlope);

      if (groupBricks.normal) {
        groupBricks.normal -= Math.floor((underGroundPercentage / 100) * groupBricks.normal);
      }

      totalBricks.normal += groupBricks.normal;
      totalBricks.half += groupBricks.half;
      totalBricks.topCover += groupBricks.topCover;
    });

    totalBricks.normal += Math.floor(connectorBricks / 2);

    return totalBricks;
  });

export const getBrickColor = createSelector([selectFence], (fence) => {
  switch (fence.settings.color) {
    case 'creamWhite':
      return {
        normal: SonevaFullCreamWhite,
        half: SonevaHalfCreamWhite,
        topCover: SonevaTopCreamWhite,
      };
    case 'gray':
      return {
        normal: SonevaFullGray,
        half: SonevaHalfGray,
        topCover: SonevaTopGray,
      };
    case 'anthracite':
      return {
        normal: SonevaFullAnthracite,
        half: SonevaHalfAnthracite,
        topCover: SonevaTopAnthracite,
      };
    case 'saharaBrown':
      return {
        normal: SonevaFullSaharaBrown,
        half: SonevaHalfSaharaBrown,
        topCover: SonevaTopSaharaBrown,
      };
    default:
      return {
        normal: SonevaFullSand,
        half: SonevaHalfSand,
        topCover: SonevaTopSand,
      };
  }
});
